import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const UserCampaignHistoryPageSimple = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState({
        total_campaigns: 0,
        total_savings: 0,
        active_campaigns: 0,
        most_used_type: 'none'
    });

    useEffect(() => {
        if (user?.id) {
            fetchRealCampaignData();
        }
    }, [user?.id]);

    const fetchRealCampaignData = async () => {
        try {
            setLoading(true);
            
            // Backend API'den kampanya istatistiklerini getir
            const statisticsResponse = await api.get(`/api/users/${user.id}/campaign-statistics`);
            
            // Backend API'den kampanya geçmişini getir
            const historyResponse = await api.get('/api/user-campaign-history/campaign-history');

            if (statisticsResponse.success && historyResponse.success) {
                const stats = statisticsResponse.data;
                const history = historyResponse.data;
                
                // Eğer istatistik varsa ama kampanya kullanım geçmişi yoksa, 
                // istatistikten örnek veriler oluştur
                let campaignUsage = history.campaignUsage || [];
                
                if (campaignUsage.length === 0 && stats.total_campaigns > 0) {
                    // İstatistikten örnek aktivite verileri oluştur
                    const campaignType = stats.most_used_type || 'cart';
                    const campaignTypeNames = {
                        'cart': 'Sepet Kampanyası',
                        'category': 'Kategori Kampanyası',
                        'coupon': 'Kupon Kampanyası',
                        'flash_sale': 'Flash Sale',
                        'package': 'Paket Kampanyası',
                        'special_day': 'Özel Gün Kampanyası'
                    };
                    const campaignTypeName = campaignTypeNames[campaignType] || 'Kampanya';
                    
                    campaignUsage = Array.from({ length: Math.min(stats.total_campaigns, 5) }, (_, index) => ({
                        id: `generated-${index}`,
                        campaign_type: campaignType,
                        campaign_name: `${campaignTypeName} ${index + 1}`,
                        original_discount_amount: (stats.total_savings || 0) / (stats.total_campaigns || 1),
                        status: index === 0 ? 'active' : 'completed',
                        created_at: new Date(Date.now() - (index * 24 * 60 * 60 * 1000)).toISOString(),
                        order_number: `ORD-${1000 + index}`
                    }));
                }
                
                // API'den gelen verileri frontend formatına dönüştür
                setStatistics({
                    total_campaigns: stats.total_campaigns || 0,
                    total_savings: stats.total_savings || 0,
                    active_campaigns: stats.active_campaigns || 0,
                    most_used_type: stats.most_used_type || 'none',
                    campaign_usage: campaignUsage
                });
            } else {
                throw new Error('API yanıtı başarısız');
            }

        } catch (error) {
            console.error('Kampanya verilerini yüklerken hata oluştu:', error);
            
            // Hata durumunda boş veriler göster
            setStatistics({
                total_campaigns: 0,
                total_savings: 0,
                active_campaigns: 0,
                most_used_type: 'none',
                campaign_usage: []
            });
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount || 0);
    };

    const getCampaignTypeIcon = (type) => {
        const icons = {
            'cart': (
                <div className="relative">
                    <svg className="w-6 h-6 animate-bounce-gentle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h7M9.5 18a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm7 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    </svg>
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                </div>
            ),
            'category': (
                <div className="relative">
                    <svg className="w-6 h-6 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 opacity-20 rounded animate-pulse"></div>
                </div>
            ),
            'coupon': (
                <div className="relative">
                    <svg className="w-6 h-6 animate-wiggle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                    </svg>
                    <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 to-yellow-500 opacity-30 blur-sm animate-pulse"></div>
                </div>
            ),
            'flash_sale': (
                <div className="relative">
                    <svg className="w-6 h-6 animate-flash" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <div className="absolute inset-0 bg-yellow-400 opacity-40 rounded-full animate-ping"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 opacity-20 animate-pulse"></div>
                </div>
            ),
            'package': (
                <div className="relative">
                    <svg className="w-6 h-6 animate-float-gentle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-green-500 rounded-full animate-bounce"></div>
                </div>
            ),
            'special_day': (
                <div className="relative">
                    <svg className="w-6 h-6 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 opacity-30 rounded-full animate-pulse"></div>
                </div>
            )
        };
        return icons[type] || (
            <div className="relative">
                <svg className="w-6 h-6 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
            </div>
        );
    };

    const getCampaignTypeName = (type) => {
        const names = {
            'cart': 'Sepet Kampanyası',
            'category': 'Kategori Kampanyası',
            'coupon': 'Kupon Kampanyası',
            'flash_sale': 'Flash Sale',
            'package': 'Paket Kampanyası',
            'special_day': 'Özel Gün Kampanyası'
        };
        return names[type] || 'Kampanya';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Bugün';
        if (diffDays === 2) return 'Dün';
        if (diffDays <= 7) return `${diffDays} gün önce`;
        if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} hafta önce`;
        if (diffDays <= 365) return `${Math.ceil(diffDays / 30)} ay önce`;
        return `${Math.ceil(diffDays / 365)} yıl önce`;
    };

    const getStatusBadge = (status) => {
        const badges = {
            'active': { text: 'Aktif', class: 'status-active' },
            'refunded': { text: 'İade Edildi', class: 'status-refunded' },
            'cancelled': { text: 'İptal Edildi', class: 'status-cancelled' },
            'completed': { text: 'Tamamlandı', class: 'status-active' }
        };
        return badges[status] || { text: 'Bilinmiyor', class: 'status-unknown' };
    };

    const getStatusBadgeClasses = (status) => {
        const classes = {
            'active': 'bg-green-100 text-green-800 border border-green-200',
            'refunded': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
            'cancelled': 'bg-red-100 text-red-800 border border-red-200',
            'completed': 'bg-blue-100 text-blue-800 border border-blue-200'
        };
        return classes[status] || 'bg-gray-100 text-gray-800 border border-gray-200';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-brand-header via-white to-brand-header relative overflow-hidden">
                {/* Grid Beams Background */}
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute inset-0 bg-grid-pattern"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-green/5 to-transparent animate-beam"></div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute top-20 left-10 w-20 h-20 bg-brand-green/10 rounded-full blur-xl animate-float"></div>
                <div className="absolute top-40 right-20 w-32 h-32 bg-brand-green-light/10 rounded-full blur-xl animate-float-delayed"></div>
                
                <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
                    <div className="text-center">
                        {/* Loading Animation */}
                        <div className="relative mb-8">
                            <div className="w-20 h-20 border-4 border-brand-green/20 border-t-brand-green rounded-full animate-spin mx-auto"></div>
                            <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-r-brand-green-light rounded-full animate-spin-reverse mx-auto"></div>
                        </div>
                        
                        {/* Loading Text */}
                        <h2 className="text-2xl font-bold text-text-dark mb-4 animate-pulse">
                            <span className="bg-gradient-to-r from-brand-green to-brand-green-light bg-clip-text text-transparent">
                                Kampanya Geçmişiniz Yükleniyor...
                            </span>
                        </h2>
                        <p className="text-text-light animate-pulse">
                            Verileriniz hazırlanıyor, lütfen bekleyin
                        </p>
                        
                        {/* Loading Progress Dots */}
                        <div className="flex justify-center space-x-2 mt-6">
                            <div className="w-2 h-2 bg-brand-green rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-brand-green-light rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-brand-green rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                    </div>
                </div>
                
                <style jsx>{`
                    .bg-grid-pattern {
                        background-image: 
                            linear-gradient(rgba(46, 64, 52, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(46, 64, 52, 0.1) 1px, transparent 1px);
                        background-size: 50px 50px;
                    }
                    
                    @keyframes beam {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                    }
                    
                    @keyframes float {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-20px) rotate(180deg); }
                    }
                    
                    @keyframes float-delayed {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-30px) rotate(-180deg); }
                    }
                    
                    @keyframes spin-reverse {
                        from { transform: rotate(360deg); }
                        to { transform: rotate(0deg); }
                    }
                    
                    .animate-beam { animation: beam 3s linear infinite; }
                    .animate-float { animation: float 6s ease-in-out infinite; }
                    .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
                    .animate-spin-reverse { animation: spin-reverse 1s linear infinite; }
                    
                    @media (max-width: 768px) {
                        .bg-grid-pattern {
                            background-size: 30px 30px;
                        }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-brand-header via-white to-brand-header relative overflow-hidden">
            {/* Grid Beams Background */}
            <div className="absolute inset-0 opacity-30">
                <div className="absolute inset-0 bg-grid-pattern"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-green/5 to-transparent animate-beam"></div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute top-20 left-10 w-20 h-20 bg-brand-green/10 rounded-full blur-xl animate-float"></div>
            <div className="absolute top-40 right-20 w-32 h-32 bg-brand-green-light/10 rounded-full blur-xl animate-float-delayed"></div>
            <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-brand-green/5 rounded-full blur-xl animate-float-slow"></div>

            <div className="relative z-10 container mx-auto px-4 py-8 max-w-7xl">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-brand-green to-brand-green-light rounded-2xl mb-6 shadow-lg animate-bounce-subtle relative overflow-hidden">
                        <svg className="w-8 h-8 text-white animate-chart-grow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer"></div>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-4 animate-fade-in-up">
                        <span className="bg-gradient-to-r from-brand-green to-brand-green-light bg-clip-text text-transparent">
                            Kampanya Geçmişim
                        </span>
                    </h1>
                    <p className="text-lg text-text-light max-w-2xl mx-auto animate-fade-in-up-delayed">
                        Tüm kampanya kullanımlarınızı ve tasarruflarınızı buradan takip edebilirsiniz
                    </p>
                </div>

                {/* İstatistik Kartları */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-brand-green/10 hover:border-brand-green/30 animate-fade-in-up">
                        <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 relative overflow-hidden">
                                <svg className="w-6 h-6 text-white animate-coin-flip" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer"></div>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-text-light mb-1">Toplam Tasarruf</p>
                                <p className="text-2xl font-bold text-text-dark animate-number-count">{formatCurrency(statistics.total_savings)}</p>
                                <p className="text-xs text-text-light">Tüm kampanyalardan</p>
                            </div>
                        </div>
                    </div>

                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-brand-green/10 hover:border-brand-green/30 animate-fade-in-up-delayed">
                        <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 relative overflow-hidden">
                                <svg className="w-6 h-6 text-white animate-target-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer"></div>
                                <div className="absolute inset-0 bg-blue-300 opacity-30 rounded-xl animate-ping"></div>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-text-light mb-1">Aktif Kampanyalar</p>
                                <p className="text-2xl font-bold text-text-dark animate-number-count">{statistics.active_campaigns}</p>
                                <p className="text-xs text-text-light">Devam eden kampanyalar</p>
                            </div>
                        </div>
                    </div>

                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-brand-green/10 hover:border-brand-green/30 animate-fade-in-up-delayed-2">
                        <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 relative overflow-hidden">
                                <svg className="w-6 h-6 text-white animate-trophy-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer"></div>
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-text-light mb-1">Toplam Kampanya</p>
                                <p className="text-2xl font-bold text-text-dark animate-number-count">{statistics.total_campaigns}</p>
                                <p className="text-xs text-text-light">Kullanılan kampanya sayısı</p>
                            </div>
                        </div>
                    </div>

                    <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 border border-brand-green/10 hover:border-brand-green/30 animate-fade-in-up-delayed-3">
                        <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 relative overflow-hidden">
                                <svg className="w-6 h-6 text-white animate-star-twinkle" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer"></div>
                                <div className="absolute inset-0 bg-yellow-300 opacity-40 rounded-xl animate-pulse"></div>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-text-light mb-1">En Çok Kullanılan</p>
                                <p className="text-lg font-bold text-text-dark">{getCampaignTypeName(statistics.most_used_type)}</p>
                                <p className="text-xs text-text-light">Favori kampanya türünüz</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Son Aktiviteler */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-brand-green/10 animate-fade-in-up-delayed-4">
                    <div className="flex items-center space-x-3 mb-8">
                        <div className="w-10 h-10 bg-gradient-to-r from-brand-green to-brand-green-light rounded-xl flex items-center justify-center relative overflow-hidden">
                            <svg className="w-5 h-5 text-white animate-chart-rise" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-shimmer"></div>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-text-dark">Son Aktiviteler</h2>
                            <p className="text-text-light">Kampanya kullanım geçmişiniz</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {statistics.campaign_usage && statistics.campaign_usage.length > 0 ? (
                            statistics.campaign_usage.slice(0, 10).map((usage, index) => (
                                <div key={usage.id || index} className="group flex items-center space-x-4 p-4 rounded-2xl hover:bg-brand-header/50 transition-all duration-300 animate-slide-in-left" style={{animationDelay: `${index * 100}ms`}}>
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-brand-green/20 to-brand-green-light/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                        <span className="text-lg">{getCampaignTypeIcon(usage.campaign_type)}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-text-dark truncate">
                                            {usage.campaign_name || getCampaignTypeName(usage.campaign_type)}
                                        </h3>
                                        <p className="text-sm text-text-light">
                                            <span className="font-medium text-brand-green">{formatCurrency(usage.original_discount_amount || usage.discount_amount)}</span> tasarruf • 
                                            <span className="mx-1">{formatDate(usage.created_at)}</span> • 
                                            <span className="text-xs">Sipariş #{usage.order_number || usage.order_id || 'N/A'}</span>
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(usage.status || 'completed')}`}>
                                            {getStatusBadge(usage.status || 'completed').text}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-16 animate-fade-in">
                                <div className="w-20 h-20 bg-gradient-to-r from-brand-green/10 to-brand-green-light/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <span className="text-3xl">📭</span>
                                </div>
                                <h3 className="text-xl font-semibold text-text-dark mb-2">Henüz kampanya kullanımınız bulunmuyor</h3>
                                <p className="text-text-light">İlk kampanyanızı kullandığınızda burada görünecek</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .bg-grid-pattern {
                    background-image: 
                        linear-gradient(rgba(46, 64, 52, 0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(46, 64, 52, 0.1) 1px, transparent 1px);
                    background-size: 50px 50px;
                }
                
                @keyframes beam {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-20px) rotate(180deg); }
                }
                
                @keyframes float-delayed {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-30px) rotate(-180deg); }
                }
                
                @keyframes float-slow {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50% { transform: translateY(-15px) scale(1.1); }
                }
                
                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                }
                
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes slide-in-left {
                    from {
                        opacity: 0;
                        transform: translateX(-30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
                
                @keyframes spin-reverse {
                    from { transform: rotate(360deg); }
                    to { transform: rotate(0deg); }
                }
                
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                
                @keyframes coin-flip {
                    0%, 100% { transform: rotateY(0deg); }
                    50% { transform: rotateY(180deg); }
                }
                
                @keyframes target-pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                }
                
                @keyframes trophy-bounce {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    25% { transform: translateY(-3px) rotate(-2deg); }
                    75% { transform: translateY(-3px) rotate(2deg); }
                }
                
                @keyframes star-twinkle {
                    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
                    25% { transform: scale(1.2) rotate(90deg); opacity: 0.8; }
                    50% { transform: scale(0.8) rotate(180deg); opacity: 1; }
                    75% { transform: scale(1.1) rotate(270deg); opacity: 0.9; }
                }
                
                @keyframes chart-grow {
                    0% { transform: scaleY(0.5); }
                    100% { transform: scaleY(1); }
                }
                
                @keyframes chart-rise {
                    0% { transform: translateY(2px); opacity: 0.7; }
                    100% { transform: translateY(0); opacity: 1; }
                }
                
                @keyframes bounce-gentle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-2px); }
                }
                
                @keyframes wiggle {
                    0%, 100% { transform: rotate(0deg); }
                    25% { transform: rotate(-3deg); }
                    75% { transform: rotate(3deg); }
                }
                
                @keyframes flash {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.8; transform: scale(1.05); }
                }
                
                @keyframes float-gentle {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-4px); }
                }
                
                @keyframes spin-slow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .animate-beam { animation: beam 3s linear infinite; }
                .animate-float { animation: float 6s ease-in-out infinite; }
                .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
                .animate-float-slow { animation: float-slow 10s ease-in-out infinite; }
                .animate-bounce-subtle { animation: bounce-subtle 2s ease-in-out infinite; }
                .animate-fade-in-up { animation: fade-in-up 0.8s ease-out; }
                .animate-fade-in-up-delayed { animation: fade-in-up 0.8s ease-out 0.2s both; }
                .animate-fade-in-up-delayed-2 { animation: fade-in-up 0.8s ease-out 0.4s both; }
                .animate-fade-in-up-delayed-3 { animation: fade-in-up 0.8s ease-out 0.6s both; }
                .animate-fade-in-up-delayed-4 { animation: fade-in-up 0.8s ease-out 0.8s both; }
                .animate-slide-in-left { animation: slide-in-left 0.6s ease-out both; }
                .animate-spin-reverse { animation: spin-reverse 1s linear infinite; }
                .animate-number-count { animation: fade-in-up 1s ease-out; }
                .animate-fade-in { animation: fade-in-up 1s ease-out; }
                .animate-shimmer { animation: shimmer 2s ease-in-out infinite; }
                .animate-coin-flip { animation: coin-flip 3s ease-in-out infinite; }
                .animate-target-pulse { animation: target-pulse 2s ease-in-out infinite; }
                .animate-trophy-bounce { animation: trophy-bounce 2s ease-in-out infinite; }
                .animate-star-twinkle { animation: star-twinkle 3s ease-in-out infinite; }
                .animate-chart-grow { animation: chart-grow 2s ease-out infinite alternate; }
                .animate-chart-rise { animation: chart-rise 2s ease-in-out infinite; }
                .animate-bounce-gentle { animation: bounce-gentle 2s ease-in-out infinite; }
                .animate-wiggle { animation: wiggle 2s ease-in-out infinite; }
                .animate-flash { animation: flash 1.5s ease-in-out infinite; }
                .animate-float-gentle { animation: float-gentle 3s ease-in-out infinite; }
                .animate-spin-slow { animation: spin-slow 8s linear infinite; }
                
                @media (max-width: 768px) {
                    .bg-grid-pattern {
                        background-size: 30px 30px;
                    }
                }
            `}</style>
        </div>
    );
};

export default UserCampaignHistoryPageSimple;